// hooks/useVehicleTracking.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTrackingProviders } from "../context/TrackingProvidersContext";
import { useNotification, NotificationSeverity } from "../context/NotificationContext";

/**
 * Hook personalizado para tracking de vehículos basado en suscripciones WebSocket
 * Reemplaza el sistema de polling por un patrón de suscriptor en tiempo real
 * 
 * @param {string} provider - ID del proveedor ('all' para todos los proveedores)
 * @returns {object} Estado y métodos para tracking vehicular
 */
export const useVehicleTracking = (provider = "all") => {
  // 🎯 Obtener contexto de tracking vehicular
  const {
    vehicles: allVehicles,
    isConnected,
    connectionStatus,
    lastUpdate,
    selectedVehicle: contextSelectedVehicle,
    trackedEvents: contextTrackedEvents,
    selectVehicle: contextSelectVehicle,
    clearSelection: contextClearSelection,
    forceUpdate: contextForceUpdate,
    getVehiclesByProvider,
    findVehicle,
    getTrackingStats,
    isLoading: contextIsLoading,
    isError: contextIsError
  } = useTrackingProviders();

  // 🔧 Estados locales para este hook específico
  const [localSelectedVehicle, setLocalSelectedVehicle] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState(new Map());
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // 📋 Servicios y contextos adicionales
  const { showNotification } = useNotification();

  /**
   * 📊 Filtrar vehículos según el proveedor seleccionado
   */
  const vehicles = useMemo(() => {
    if (!allVehicles || allVehicles.length === 0) return [];
    
    if (provider === "all") {
      return allVehicles;
    }
    
    return getVehiclesByProvider(provider);
  }, [allVehicles, provider, getVehiclesByProvider]);

  /**
   * 🎯 Gestión de vehículo seleccionado local vs contexto global
   */
  const selectedVehicle = localSelectedVehicle || contextSelectedVehicle;

  /**
   * 📡 Eventos filtrados según configuración
   */
  const trackedEvents = useMemo(() => {
    if (provider === "all") {
      return contextTrackedEvents;
    }
    
    // Filtrar eventos por proveedor específico
    return contextTrackedEvents.filter(event => event.provider === provider);
  }, [contextTrackedEvents, provider]);

  /**
   * 🔍 Seleccionar vehículo con opciones locales o globales
   */
  const selectVehicle = useCallback((vehicle, useGlobalSelection = false) => {
    if (useGlobalSelection) {
      // Usar selección global del contexto
      contextSelectVehicle(vehicle);
      setLocalSelectedVehicle(null);
    } else {
      // Usar selección local de este hook
      setLocalSelectedVehicle(vehicle);
    }
  }, [contextSelectVehicle]);

  /**
   * 🧹 Limpiar selección de vehículo
   */
  const clearSelection = useCallback(() => {
    setLocalSelectedVehicle(null);
    contextClearSelection();
  }, [contextClearSelection]);

  /**
   * 📈 Obtener historial de vehículo (solo para Securita por ahora)
   */
  const getVehicleHistory = useCallback(async (vehicleId, startDate, endDate) => {
    if (provider !== "securita" && provider !== "all") {
      throw new Error(`El proveedor ${provider} no soporta historial`);
    }

    setLoadingHistory(true);
    
    try {
      // Para implementar: llamada a API de historial
      // Por ahora retornamos datos mockados
      const historyData = {
        vehicleId,
        startDate,
        endDate,
        points: [], // Implementar llamada real a API
        provider: "securita"
      };

      // Guardar en caché local
      const cacheKey = `${vehicleId}_${startDate}_${endDate}`;
      setVehicleHistory(prev => new Map(prev).set(cacheKey, historyData));

      showNotification({
        message: `Historial obtenido para ${vehicleId}`,
        severity: NotificationSeverity.SUCCESS
      });

      return historyData;
    } catch (error) {
      showNotification({
        message: `Error al obtener historial: ${error.message}`,
        severity: NotificationSeverity.ERROR
      });
      throw error;
    } finally {
      setLoadingHistory(false);
    }
  }, [provider, showNotification]);

  /**
   * 🔄 Forzar actualización con manejo de errores mejorado
   */
  const forceUpdate = useCallback(async () => {
    try {
      await contextForceUpdate();
      
      showNotification({
        message: "Posiciones actualizadas exitosamente",
        severity: NotificationSeverity.SUCCESS
      });
    } catch (error) {
      showNotification({
        message: `Error al actualizar posiciones: ${error.message}`,
        severity: NotificationSeverity.ERROR
      });
    }
  }, [contextForceUpdate, showNotification]);

  /**
   * 🔍 Buscar vehículo por ID
   */
  const findVehicleById = useCallback((vehicleId) => {
    return findVehicle(vehicleId, provider === "all" ? null : provider);
  }, [findVehicle, provider]);

  /**
   * 📊 Estadísticas específicas del proveedor
   */
  const providerStats = useMemo(() => {
    const allStats = getTrackingStats();
    
    if (provider === "all") {
      return allStats;
    }
    
    // Filtrar estadísticas por proveedor específico
    const providerVehicles = vehicles;
    const activeVehicles = providerVehicles.filter(v => 
      (v.GpsSpeed && parseFloat(v.GpsSpeed) > 0) || v.Ignition === 1
    ).length;

    return {
      totalVehicles: providerVehicles.length,
      activeVehicles,
      providerStats: {
        [provider]: {
          total: providerVehicles.length,
          active: activeVehicles
        }
      },
      lastUpdate: allStats.lastUpdate,
      connectionStatus: allStats.connectionStatus,
      eventsCount: trackedEvents.length
    };
  }, [getTrackingStats, provider, vehicles, trackedEvents.length]);

  /**
   * 🔄 Efecto para manejar cambios de proveedor
   */
  useEffect(() => {
    // Limpiar selección local cuando cambie el proveedor
    setLocalSelectedVehicle(null);
    
    // Limpiar caché de historial irrelevante
    setVehicleHistory(prev => {
      const newHistory = new Map();
      // Mantener solo historiales del proveedor actual
      for (const [key, value] of prev) {
        if (provider === "all" || value.provider === provider) {
          newHistory.set(key, value);
        }
      }
      return newHistory;
    });
  }, [provider]);

  /**
   * 📊 Estados derivados para compatibilidad con código existente
   */
  const isLoading = contextIsLoading || loadingHistory;
  const isError = contextIsError;
  const error = contextIsError ? new Error('Error de conexión con tracking') : null;

  // 🎯 Retorno del hook con API completa
  return {
    // 📊 Datos principales
    vehicles,
    isLoading,
    isError,
    error,
    
    // 🎯 Selección de vehículos
    selectedVehicle,
    selectVehicle,
    clearSelection,
    
    // 📈 Operaciones de datos
    getVehicleHistory,
    forceUpdate,
    findVehicleById,
    
    // 📡 Eventos y estado de conexión
    trackedEvents,
    isConnected,
    connectionStatus,
    lastUpdate,
    
    // 📊 Estadísticas y metadatos
    providerStats,
    
    // 🔧 Estados específicos
    loadingHistory,
    vehicleHistory: vehicleHistory,
    
    // 📦 Datos adicionales para compatibilidad
    refetch: forceUpdate, // Alias para compatibilidad
    refetchPositions: forceUpdate, // Alias para compatibilidad
    
    // 🎯 Métodos de utilidad
    getTotalVehicles: () => vehicles.length,
    getActiveVehicles: () => vehicles.filter(v => 
      (v.GpsSpeed && parseFloat(v.GpsSpeed) > 0) || v.Ignition === 1
    ).length,
    
    // 🔄 Estado de sincronización
    isSynced: isConnected && !isLoading,
    lastSyncTime: lastUpdate
  };
};